import { DocumentDto } from 'src/api/FiretrolApiClient';
import type { IFolderNode } from 'src/app/documents/folderNode';
import type { AccessKind } from 'src/design/molecules/AccessIndicator';
import { IAvatarGroupItem } from 'src/design/molecules/AvatarGroup';
import { DOCUMENT_ACCESS } from 'src/app/documents/documentConstants';

/** @registry Display name for a user in the Documents module: the signed-in user is always "You", an unresolved user is "Unknown". */
export const userDisplayName = (
  userId: number | undefined,
  fullName: string | undefined,
  currentUserId: number | undefined,
): string =>
  userId != null && userId === currentUserId ? 'You' : fullName ?? 'Unknown';

export interface IDocumentAccess {
  kind: AccessKind;
  people: IAvatarGroupItem[];
}

/** @registry Maps a folder's access settings to the AccessIndicator inputs (kind + avatar people), naming the current user "You" and dropping shares whose user did not resolve. */
export const accessKindForFolder = (
  folder: IFolderNode,
  currentUserId: number,
): IDocumentAccess => {
  if (folder.access === DOCUMENT_ACCESS.all) {
    return { kind: 'everyone', people: [] };
  }

  const sharedWith = folder.sharedWith ?? [];

  if (folder.access === DOCUMENT_ACCESS.onlyYou || sharedWith.length === 0) {
    return { kind: 'onlyYou', people: [] };
  }

  const people: IAvatarGroupItem[] = sharedWith
    .filter((entry) => entry.user != null)
    .map((entry) => ({
      key: String(entry.userId),
      name: userDisplayName(entry.userId, entry.user?.fullName, currentUserId),
    }));

  if (people.length === 0) {
    return { kind: 'onlyYou', people: [] };
  }

  return { kind: 'people', people };
};

/** @registry Maps a document's parent folder access settings to the AccessIndicator inputs (kind + avatar people); a document with no parent folder inherits job-wide access. */
export const accessKindForDocument = (
  document: DocumentDto,
  currentUserId: number,
): IDocumentAccess =>
  document.parent
    ? accessKindForFolder(document.parent, currentUserId)
    : { kind: 'everyone', people: [] };
